/*
 * Copyright (c) 2025.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import React from 'react';
import PropTypes from 'prop-types';
import DialogButton from "./UIGeneratorComponents/DialogButton.jsx";
import UiElementInlineError from "./ErrorHandling/UIElementInlineError.jsx";
import {ErrorBoundary} from "errorboundarys";
import MotionDiv from "./Animations/MotionDiv.jsx";
import CloseButton from "./CloseButton.jsx";
import {Shortcuts} from "../Shortcuts.js";


class SlideOverComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            didSetFocus: false
        };

        this.body = React.createRef()
        this.shortcuts = new Shortcuts()
    }

    componentDidMount() {

        this.shortcuts.register("Save Dialog", "General", ['ctrl+enter', 'command+enter', 'ctrl+s', 'command+s'], (event) => {
            let {isTopElement, button = []} = this.props

            event.preventDefault()
            event.stopPropagation()
            if (isTopElement) {
                let saveButton = button.find(item => (item.type === "save"))

                if (saveButton?.onClick !== undefined) {
                    saveButton.onClick()
                }
            }

        })

        this.shortcuts.register("Close Dialog", "General", ['ctrl+esc', 'command+esc'], (event) => {
            let {isTopElement, button = []} = this.props

            event.preventDefault()
            event.stopPropagation()
            if (isTopElement) {
                let saveButton = button.find(item => (item.type === "abort"))

                if (saveButton?.onClick !== undefined) {
                    saveButton.onClick()
                }
            }
        })

        if (!this.state.didSetFocus && this.props.setFocus) {
            setTimeout(() => {
                if (this.body?.current !== undefined && this.body?.current !== null) {
                    this.body.current.focus()
                    this.setState({didSetFocus: true})
                }

            }, 200)
        }

    }

    componentWillUnmount() {
        this.shortcuts.unmount()
    }


    render() {
        let {isOpen, uiId, position} = this.props

        return (
            <div className={"fixed inset-0 overflow-hidden pointer-events-none z-30"}>
                <div className={"absolute inset-0 overflow-hidden z-30"}>
                    {/*Gray out the left site of the Page*/}
                    {(this.props.withBackgroundOverlay && isOpen) && (<MotionDiv
                            initial={{opacity: 0}}
                            animate={{opacity: 0.6}}
                            exit={{opacity: 0}}
                            className={`absolute inset-0 bg-stone-500 opacity-40 w-full h-full  pointer-events-none  `}
                        />
                    )}
                    {/*The Actual Panel*/}

                    {isOpen && (<>

                        <MotionDiv
                            key={uiId}
                            initial={{x: (position === "left") ? (-200) : (200), opacity: 0.9}}
                            animate={{
                                x: 0,
                                opacity: 1,
                                zIndex: 10,

                            }}
                            exit={{
                                x: (position === "left") ? (-200) : (200),
                                opacity: 0,
                                zIndex: 10,
                            }}
                            transition={{type: "spring", stiffness: 200, damping: 20, duration: 0.4}}
                            className={`absolute inset-y-0 ${(position === "left") ? ("left-0") : ("right-0")}  pl-2 max-w-full flex pointer-events-auto`}
                        >


                            <div
                                className={"w-screen " + this.props.width + "   pointer-events-auto  p-3  "}>
                                <div
                                    className="h-full flex flex-col  bg-white shadow-xl   rounded-lg">
                                    <div className="pl-6 border-b  h-14">
                                        <div className="flex items-start justify-between py-4 ">
                                            <h2
                                                className="text-base font-semibold text-gray-900 truncate">
                                                {this.props.title}
                                            </h2>
                                            <CloseButton clickZoneClassname={"pl-3 pr-3"} onClick={() => {
                                                // this is a bigger target for the close button to make it easey to hit
                                                if (this.props.onClose !== undefined) {
                                                    this.props.onClose()
                                                }
                                            }}/>

                                        </div>
                                    </div>
                                    <div
                                        className="relative flex-1 px-4 overflow-y-auto customScrollbar focus:outline-none"
                                        ref={this.body} tabIndex={"-1"}>
                                        {/* content */}
                                        <ErrorBoundary errorStyle={'small'}>
                                            {this.props.children}
                                        </ErrorBoundary>

                                    </div>

                                    <UiElementInlineError uiId={this.props.uiId}/>

                                    <DialogButton button={this.props.button}/>

                                </div>

                            </div>
                        </MotionDiv>

                    </>)}


                </div>
            </div>
        )
    };
}

export default SlideOverComponent;

SlideOverComponent.propTypes = {

    children: PropTypes.any,
    /**
     * Bool value to open the panel
     * @default false
     */
    isOpen: PropTypes.bool,
    /**
     * A Tailwind CSS width Class
     * @example max-w-sm
     * @default max-w-md
     */
    width: PropTypes.oneOf(['max-w-xs', 'max-w-sm', 'max-w-md', 'max-w-lg', 'max-w-xl', 'max-w-2xl', 'max-w-3xl', 'max-w-4xl', 'max-w-5xl', 'max-w-6xl', 'max-w-7xl', 'max-w-full']),
    title: PropTypes.string,
    /**
     * A Bool value to decide weather the left still should be still usable if the panel is open
     * @default false
     */
    withBackgroundOverlay: PropTypes.bool,
    onClose: PropTypes.func,
    /**
     * This contains the elements meant for the button row
     */
    button: PropTypes.array,
    setFocus: PropTypes.bool
}

SlideOverComponent.defaultProps = {
    title: 'Panel title',
    width: 'max-w-md',
    isOpen: false,
    setFocus: true,
    withBackgroundOverlay: false,
}